/**
 * byo-v4-core.js — Build Your Own v4, config only.
 *
 * P0 scope, and nothing beyond it: find the one config blob written by
 * snippets/byo-v4-config.liquid, parse it, and expose it as window.BYO.
 * No builder, no fetching, no DOM beyond reading that script tag.
 *
 * The blob is the single source of settings (CONFIG-SCHEMA.md §9). Nothing in
 * this build may read a theme setting from anywhere else.
 *
 * ES2020 module, no transpilation, no bundler. Loaded as
 * <script type="module" src="..."> from sections/byo-v4-builder.liquid.
 */

export const VERSION = '4.0.0-p0';

export const CONFIG_SELECTOR = '[data-byo-v4-config]';

export const FAQ_SELECTOR = '[data-byo-v4-faqs]';

/**
 * Read and parse the config blob.
 *
 * @param {ParentNode} [root=document] where to look for the blob.
 * @returns {object|null} the parsed config, or null if it is absent or invalid.
 */
export function readConfig(root = document) {
  const el = root.querySelector(CONFIG_SELECTOR);

  if (!el) {
    console.warn('[byo-v4] no ' + CONFIG_SELECTOR + ' on the page.');
    return null;
  }

  try {
    return JSON.parse(el.textContent);
  } catch (error) {
    console.error('[byo-v4] the config blob is not valid JSON.', error);
    return null;
  }
}

/**
 * Read the FAQ blob and fold it into the config.
 *
 * The questions live on sections/byo-v4-faq.liquid, and a snippet rendered from
 * byo-v4-builder cannot reach another section's blocks, so the builder's own
 * blob ships "faqs": [] and the FAQ section emits its own. Merging them here is
 * what makes CONFIG-SCHEMA.md §9's single `config.faqs` true at runtime.
 * DEVIATIONS.md D6.
 *
 * @param {object|null} target the parsed config, mutated in place.
 * @param {ParentNode} [root=document] where to look for the blob.
 * @returns {object|null} the same config.
 */
export function mergeFaqs(target, root = document) {
  if (!target) return target;

  const el = root.querySelector(FAQ_SELECTOR);
  if (!el) return target;

  try {
    const faqs = JSON.parse(el.textContent);
    if (Array.isArray(faqs) && faqs.length > 0) target.faqs = faqs;
  } catch (error) {
    console.error('[byo-v4] the FAQ blob is not valid JSON.', error);
  }

  return target;
}

const config = mergeFaqs(readConfig());

/**
 * window.BYO — the config, and how to re-read it.
 *
 * Section rendering in the theme editor replaces the section's DOM, which
 * replaces the blob, so reload() exists for that and for nothing else.
 */
window.BYO = {
  version: VERSION,
  config,
  readConfig,
  mergeFaqs,
  reload() {
    window.BYO.config = mergeFaqs(readConfig());
    return window.BYO.config;
  },
};

/* The theme editor re-renders a section on every edit, which replaces the very
 * script tags this module read at load. Re-read them so window.BYO.config is
 * never stale while someone is editing. */
document.addEventListener('shopify:section:load', () => {
  window.BYO.reload();
});

/* =========================================================================
 * P2 — data contract + engine
 * ========================================================================= */

/**
 * Money, ported literally from `reference/byo-builder/designs/byo-core-v2.jsx`
 * (Wrapt is GBP-only; the design hardcodes £, so this does too — no
 * `shop.money_format` "improvement").
 */
export function money(pence) {
  return '£' + (pence / 100).toFixed(2);
}

export function moneyShort(pence) {
  return pence % 100 ? money(pence) : '£' + pence / 100;
}

/* Alan, 26 Sep 2026: "the titles and the copy should always spell out numbers
   and not write numbers with numbers" (D70). A count in a sentence is a word;
   prices, points, dates, times and the quantity in a stepper stay figures.
   Anything that is not a whole number from 0 to 99 comes back as figures,
   which is right for "3½" and for anything too big to read well as words. */
const ONES = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
const TENS = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];

export function words(n) {
  if (!Number.isInteger(n) || n < 0 || n > 99) return String(n);
  if (n < 20) return ONES[n];
  return TENS[Math.floor(n / 10)] + (n % 10 ? `-${ONES[n % 10]}` : '');
}

/** `words`, capitalised, for the start of a sentence: "Three items". */
export function Words(n) {
  const w = words(n);
  return w.charAt(0).toUpperCase() + w.slice(1);
}

/** "one item", "three items" — a count and its noun, pluralised. */
export function countOf(n, one, many) {
  return `${words(n)} ${n === 1 ? one : many || `${one}s`}`;
}

/**
 * The property names the personal touches were written under.
 *
 * They are not fixed: a field's title comes from Giftship's own form, so the
 * date key is "Open on" by default and could be anything a merchant names it
 * (`propertyKeys` in byo-v4-submit.js matches rather than names them).
 *
 * The date pattern carries "keep wrapt until" because that is what the field is
 * actually called on builder 2484, the new BYO journey -- and it contains
 * neither "date" nor "open". Without it the match fails, v4 falls back to
 * writing its own "Open on" key, the value stops being Giftship form data and
 * quietly never reaches the printed order form. "wrapt?" covers both the brand
 * spelling and the plain one.
 * Reading them back by the same patterns is the only way the cart, the builder
 * and the write stay in step — and it lives here, once, because two copies of
 * this table drifting apart is exactly how a touch goes missing.
 */
export const TOUCH_RE = {
  photo: /photo/i,
  song: /song/i,
  message: /message/i,
  from: /^from$/i,
  recipient: /recipient|who|^for$/i,
  date: /date|open|keep\s*wrapt?\s*until/i,
  wrap: /choose your wrap/i,
  /* D70: how the box is given, when it is given in person. */
  inPerson: /in\s*person/i,
};

/** The first public property whose name matches, as written. */
export function findProp(pub, re) {
  const key = Object.keys(pub).find((k) => re.test(k.trim()));
  return key ? pub[key] : null;
}

/**
 * Every public property of a cart line, flattened, with the private `_gs_*`
 * ones left out. Arrays are joined because the wrap arrives as `["Sea &
 * Spinach"]` and reads as a value everywhere else.
 */
export function publicProps(line) {
  const pub = {};
  Object.entries((line && line.properties) || {}).forEach(([k, v]) => {
    if (!k || k.charAt(0) === '_' || v == null || v === '') return;
    pub[k] = Array.isArray(v) ? v.join(', ') : String(v);
  });
  return pub;
}

/**
 * The touches on a cart line, by meaning rather than by key — so a box can be
 * rebuilt from the line it was written to.
 */
export function readTouchProps(line) {
  const pub = publicProps(line);
  return {
    wrap: findProp(pub, TOUCH_RE.wrap),
    photo: findProp(pub, TOUCH_RE.photo),
    song: findProp(pub, TOUCH_RE.song),
    date: findProp(pub, TOUCH_RE.date),
    message: findProp(pub, TOUCH_RE.message),
    from: findProp(pub, TOUCH_RE.from),
    recipient: findProp(pub, TOUCH_RE.recipient),
    /* By value, not by key: Giftship's radio is called "Gift In Person" and
       also holds "Send Directly To Them" (D72). */
    inPerson: (() => {
      const v = findProp(pub, TOUCH_RE.inPerson);
      return v && /in\s*person|^yes/i.test(v) ? v : null;
    })(),
  };
}

/** Spaces a size costs, and the "3½" formatting for a fractional total. */
export function slotsOf(size, cfg) {
  return (cfg || config).sizes[size];
}

export function fmtSlots(n) {
  const whole = Math.floor(n + 1e-9);
  const half = n - whole >= 0.5 - 1e-9;
  return (whole || !half ? String(whole) : '') + (half ? '½' : '');
}

export function slotWord(size, cfg) {
  const n = slotsOf(size, cfg);
  return fmtSlots(n) + (n === 1 ? ' space' : ' spaces');
}

/* ---- normalise ---------------------------------------------------------
 * templates/collection.byo-v4.liquid emits raw products per collection. Size
 * resolution (metafield first, tag fallback if config.sizeFromTags is on —
 * SPIKE-FINDINGS.md C15, off by default) and the drop of unavailable/unsized
 * products both happen here, once, so every caller sees the same shape
 * (DESIGN-SPEC.md §3.1). "collections" is not in the raw feed — a request is
 * already scoped to one collection handle — so normaliseCollection() tags it
 * on, and mergeProducts() unions it across the pills fetched for one page.
 */

const SIZE_NAMES = ['Small', 'Medium', 'Large', 'Extra Large'];
const TAG_SIZE_RE = /^(?:size[-:]?)?(small|medium|large|extra[- ]?large)$/i;
const TAG_SIZE_MAP = { small: 'Small', medium: 'Medium', large: 'Large', extralarge: 'Extra Large' };

function resolveSize(raw, cfg) {
  if (raw.sizeRaw && SIZE_NAMES.includes(raw.sizeRaw)) return raw.sizeRaw;
  if (!cfg.sizeFromTags) return null;

  for (const tag of Array.isArray(raw.tags) ? raw.tags : []) {
    const m = TAG_SIZE_RE.exec(String(tag).trim());
    if (m) {
      const mapped = TAG_SIZE_MAP[m[1].toLowerCase().replace(/[- ]/g, '')];
      if (mapped) return mapped;
    }
  }
  return null;
}

function parseMaybeJson(value) {
  if (value == null) return null;
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

/**
 * One raw product → the target shape, or null if it must be dropped
 * (unavailable, or no resolvable size).
 */
export function normaliseProduct(raw, collectionHandle, cfg) {
  cfg = cfg || config;
  if (!raw.available) return null;

  const size = resolveSize(raw, cfg);
  if (!size) return null;

  return {
    id: raw.handle,
    handle: raw.handle,
    title: raw.title,
    vendor: raw.vendor,
    price: raw.price,
    size,
    collections: collectionHandle ? [collectionHandle] : [],
    dietaries: Array.isArray(raw.dietaries) ? raw.dietaries : [],
    img: raw.img || null,
    images: Array.isArray(raw.images) ? raw.images : [],
    desc: raw.desc || null,
    details: raw.details
      ? {
          specs: parseMaybeJson(raw.details.specs),
          ingredients: parseMaybeJson(raw.details.ingredients),
          allergens: parseMaybeJson(raw.details.allergens),
          nutrition: parseMaybeJson(raw.details.nutrition),
          notes: parseMaybeJson(raw.details.notes),
          usage: parseMaybeJson(raw.details.usage),
          pairing: parseMaybeJson(raw.details.pairing),
          brand: parseMaybeJson(raw.details.brand),
          /* "Why we love it", custom.product_info: plain text (D105). */
          why: raw.details.why ? String(raw.details.why).trim() || null : null,
        }
      : null,
    variantId: raw.variantId,
    available: raw.available,
  };
}

/**
 * A parsed collection.byo-v4 response → normalised products, and a console
 * log of the counts BUILD-PLAN's P2 exit criteria asks for.
 */
export function normaliseCollection(json, cfg) {
  cfg = cfg || config;
  const raw = Array.isArray(json.products) ? json.products : [];
  /* Each product remembers where it sat in this collection, which is the
     order the merchant set in the admin (D70). The merge below keeps one copy
     of a product that is in several collections, so without this a pill drew
     its products in whichever collection happened to load them first. */
  const products = raw
    .map((p, i) => {
      const n = normaliseProduct(p, json.handle, cfg);
      if (n && json.handle) n.order = { [json.handle]: i };
      return n;
    })
    .filter(Boolean);

  console.info(
    `[byo-v4] ${json.handle}: ${raw.length} in collection, ${products.length} sized+available, ${raw.length - products.length} excluded`
  );

  return products;
}

/** Union normalised product lists (from several pills) by handle, merging `collections`. */
export function mergeProducts(lists) {
  const byId = new Map();
  for (const list of lists) {
    for (const p of list) {
      const existing = byId.get(p.id);
      if (existing) {
        existing.collections = Array.from(new Set([...existing.collections, ...p.collections]));
        existing.order = { ...(existing.order || {}), ...(p.order || {}) };
      } else {
        byId.set(p.id, { ...p, collections: [...p.collections], order: { ...(p.order || {}) } });
      }
    }
  }
  return Array.from(byId.values());
}

/* ---- packing engine ------------------------------------------------------
 * BUILD-PLAN.md P2, exact: capacity spaces, item count gates the minimum
 * (SPIKE-FINDINGS.md C3/C4). An item fits only if it fits the spaces left AND
 * leaves room for the remaining items still needed at the smallest size —
 * that second clause is why a box can never end up full but short of the
 * minimum.
 */
export function createPacker(cfg) {
  cfg = cfg || config;
  const capacity = cfg.capacity;
  const minItems = cfg.minItems;
  const sizeCosts = cfg.sizes;
  const minCost = Math.min(...Object.values(sizeCosts));

  const slotsUsed = (box, byId) => box.items.reduce((s, it) => s + it.qty * sizeCosts[byId[it.id].size], 0);
  const remaining = (box, byId) => Math.max(0, capacity - slotsUsed(box, byId));
  const itemCount = (box) => box.items.reduce((s, it) => s + it.qty, 0);

  function fitsSize(size, box, byId) {
    const cost = sizeCosts[size];
    const rem = remaining(box, byId);
    if (cost > rem + 1e-9) return false;
    const stillNeed = minItems - (itemCount(box) + 1);
    if (stillNeed <= 0) return true;
    return rem - cost + 1e-9 >= stillNeed * minCost;
  }

  const fits = (product, box, byId) => fitsSize(product.size, box, byId);
  const sizesThatFit = (box, byId) => Object.keys(sizeCosts).filter((size) => fitsSize(size, box, byId));

  // Refusal copy stays short — the toast/shake text. `oops` is the long reasoning for the help sheet only.
  /* D70: say why it will not fit — the box already has things in it — rather
     than "Won't fit", which read as though the product itself were wrong. */
  function reason(product, box, byId) {
    if (fits(product, box, byId)) return null;
    return remaining(box, byId) <= 1e-9 ? 'Box full' : 'Box too full';
  }

  function oops(product, box, byId) {
    const fitList = sizesThatFit(box, byId);
    const list = fitList
      .slice()
      .reverse()
      .map((s) => s.toLowerCase())
      .join(' or ');
    const an = /^[aeiou]/.test(list) ? 'an' : 'a';
    const rem = remaining(box, byId);
    if (rem <= 1e-9 || !fitList.length) return 'Your box is full. Take something out to make room.';
    if (sizeCosts[product.size] <= rem + 1e-9) return `Your box would be too full to reach ${words(minItems)} items. Try ${an} ${list} item instead.`;
    return `Your box is too full for this one. Try ${an} ${list} item instead.`;
  }

  return { capacity, minItems, sizeCosts, minCost, slotsUsed, remaining, itemCount, fitsSize, fits, sizesThatFit, reason, oops };
}

/* ---- box state store -----------------------------------------------------
 * DESIGN-SPEC.md §3.1's EMPTY_BOX and derived values, plus a minimal
 * subscribe/notify so P3/P4 UI can render off it without re-deriving by hand.
 */
export function emptyBox() {
  return {
    items: [],
    inPerson: false,
    keepsake: false,
    wrap: null,
    photo: { on: false, file: null },
    song: { on: false, text: '' },
    date: { on: false, value: '' },
    messageOn: false,
    message: '',
    from: '',
    recipient: '',
  };
}

export function createBoxStore({ cfg, productsById = {} } = {}) {
  cfg = cfg || config;
  const packer = createPacker(cfg);
  let box = emptyBox();
  const subscribers = new Set();

  function derive() {
    const used = packer.slotsUsed(box, productsById);
    const remaining = packer.remaining(box, productsById);
    const itemCount = packer.itemCount(box);
    const itemsTotal = box.items.reduce((s, it) => s + it.qty * (productsById[it.id] ? productsById[it.id].price : 0), 0);
    const extrasTotal = (box.photo.on ? cfg.extras.photo.price : 0) + (box.song.on ? cfg.extras.song.price : 0);

    /* D70, D95: the keepsake box comes with any box that fills this many
       spaces, however it is given. Alan, 29 Sep 2026: "it should just be
       gated behind whether or not there are 5 or more spaces being used";
       sending it directly or giving it in person no longer decides this, and
       since D97 (30 Sep) the builder does not ask it. Below the line nothing mentions a
       keepsake box at all, so a customer with a smaller box never learns there
       was one to miss. It is worked out here rather than stored, so taking an
       item out takes the keepsake box with it, and putting one back returns
       it, with the wrap they had picked. */
    const keepsakeMin = typeof cfg.keepsakeMinSlots === 'number' ? cfg.keepsakeMinSlots : 5;
    const keepsakeOffered = used + 1e-9 >= keepsakeMin;
    const view = { ...box, keepsake: keepsakeOffered };
    const keepsakeTotal = view.keepsake ? cfg.extras.keepsake.price : 0;
    const total = cfg.base + itemsTotal + keepsakeTotal + extrasTotal;
    const toFree = Math.max(0, cfg.freeShipping - total);

    const meetsMin = itemCount >= cfg.minItems;
    const photoOk = !box.photo.on || !!box.photo.file;
    const songOk = !box.song.on || (box.song.text.trim().length > 0 && box.song.text.length <= cfg.limits.song);
    const dateOk = !box.date.on || !!box.date.value;
    /* The design gates an empty message: `byo-stages-v3.jsx:275` computes
       `mErr = showErrors && messageOn && !message.trim()`, and :7 hints "Write
       your gift message". This only checked the upper bound, so switching Gift
       Message on and writing nothing walked straight past Personalise — every
       other piece of that rule (the error dot, the row opening itself, the
       gate reason) was already built and simply never fired. Trimmed, so a
       message of spaces does not count either. */
    const messageOk =
      !box.messageOn || (box.message.trim().length > 0 && box.message.length <= cfg.limits.message);
    const wrapOk = !view.keepsake || box.wrap != null;
    const touchesOk = photoOk && songOk && dateOk && messageOk;

    const blockers = [];
    if (!meetsMin) blockers.push(`Add ${countOf(cfg.minItems - itemCount, 'more item', 'more items')}`);
    if (!photoOk) blockers.push('Add a photo, or switch the photo extra off');
    if (!songOk) blockers.push('Add song text, or switch the song extra off');
    if (!dateOk) blockers.push('Pick a date, or switch the date off');
    if (!messageOk) blockers.push(box.message.trim() ? 'Shorten the message' : 'Write your gift message, or switch it off');
    if (!wrapOk) blockers.push('Choose a wrap for the keepsake box');

    const touchCount = [box.photo.on, box.song.on, box.date.on, box.messageOn, view.keepsake].filter(Boolean).length;
    const units = box.items.flatMap((it) => Array.from({ length: it.qty }, () => productsById[it.id]));

    return {
      box: view,
      keepsakeOffered,
      slotsUsed: used,
      remaining,
      itemCount,
      itemsTotal,
      extrasTotal,
      keepsakeTotal,
      total,
      toFree,
      meetsMin,
      photoOk,
      songOk,
      dateOk,
      messageOk,
      touchesOk,
      wrapOk,
      canSubmit: meetsMin && touchesOk && wrapOk,
      blockers,
      touchCount,
      units,
    };
  }

  function notify() {
    const state = derive();
    subscribers.forEach((fn) => fn(state));
    return state;
  }

  return {
    packer,
    setProducts(map) {
      productsById = map;
      return notify();
    },
    getState: derive,
    subscribe(fn) {
      subscribers.add(fn);
      fn(derive());
      return () => subscribers.delete(fn);
    },
    addItem(product) {
      if (!packer.fits(product, box, productsById)) {
        return { ok: false, reason: packer.reason(product, box, productsById), oops: packer.oops(product, box, productsById) };
      }
      const existing = box.items.find((it) => it.id === product.id);
      box = {
        ...box,
        items: existing
          ? box.items.map((it) => (it.id === product.id ? { ...it, qty: it.qty + 1 } : it))
          : [...box.items, { id: product.id, qty: 1 }],
      };
      return { ok: true, state: notify() };
    },
    removeItem(id) {
      box = { ...box, items: box.items.map((it) => (it.id === id ? { ...it, qty: it.qty - 1 } : it)).filter((it) => it.qty > 0) };
      return notify();
    },
    setQty(id, qty) {
      box = {
        ...box,
        items: qty <= 0 ? box.items.filter((it) => it.id !== id) : box.items.map((it) => (it.id === id ? { ...it, qty } : it)),
      };
      return notify();
    },
    toggle(key) {
      box = { ...box, [key]: !box[key] };
      return notify();
    },
    setWrap(name) {
      box = { ...box, wrap: name };
      return notify();
    },
    /* Set, not toggled: a toggle read against a stale render flips the wrong
       way, and edit mode sets this straight after a reset. */
    setInPerson(v) {
      box = { ...box, inPerson: !!v };
      return notify();
    },
    setPhoto(patch) {
      box = { ...box, photo: { ...box.photo, ...patch } };
      return notify();
    },
    setSong(patch) {
      box = { ...box, song: { ...box.song, ...patch } };
      return notify();
    },
    setDate(patch) {
      box = { ...box, date: { ...box.date, ...patch } };
      return notify();
    },
    setMessage(text) {
      box = { ...box, message: text };
      return notify();
    },
    setFrom(text) {
      box = { ...box, from: text };
      return notify();
    },
    setRecipient(text) {
      box = { ...box, recipient: text };
      return notify();
    },
    reset() {
      box = emptyBox();
      return notify();
    },
  };
}

Object.assign(window.BYO, {
  words,
  Words,
  countOf,
  TOUCH_RE,
  findProp,
  publicProps,
  readTouchProps,
  money,
  moneyShort,
  slotsOf,
  fmtSlots,
  slotWord,
  normaliseProduct,
  normaliseCollection,
  mergeProducts,
  createPacker,
  createBoxStore,
  emptyBox,
});

/**
 * `?byoqa=engine` — the six cases BUILD-PLAN.md P2's exit criteria names.
 * Prints pass/fail to console and leaves the results on
 * window.__BYOQA_ENGINE__ for a driver script to read without re-parsing
 * console output.
 */
function runEngineSelfTest() {
  const cfg = { capacity: 8, minItems: 4, sizes: { Small: 1, Medium: 2, Large: 3, 'Extra Large': 4 } };
  const packer = createPacker(cfg);
  const results = [];
  const check = (name, pass) => results.push({ name, pass: !!pass });

  check('empty box takes any size', Object.keys(cfg.sizes).every((size) => packer.fitsSize(size, emptyBox(), {})));

  {
    // 8 spaces of Smalls: a box of 8 Smalls exactly fills capacity 8.
    const byId = { small: { size: 'Small' } };
    const box = { ...emptyBox(), items: [{ id: 'small', qty: 8 }] };
    check('8 spaces of Smalls fills the box', packer.remaining(box, byId) === 0);
  }

  {
    // XL + XL = 4 + 4 = 8 = capacity, full.
    const byId = { xl: { size: 'Extra Large' } };
    const box = { ...emptyBox(), items: [{ id: 'xl', qty: 2 }] };
    check('XL+XL = full', packer.remaining(box, byId) === 0);
  }

  {
    // 3 items (XL + Medium + Small = 4+2+1 = 7 slots) leaves 1 space; a Medium (cost 2) cannot fit.
    const byId = { a: { size: 'Extra Large' }, b: { size: 'Medium' }, c: { size: 'Small' } };
    const box = { ...emptyBox(), items: [{ id: 'a', qty: 1 }, { id: 'b', qty: 1 }, { id: 'c', qty: 1 }] };
    check('3 items in with 1 space left refuses a Medium', packer.remaining(box, byId) === 1 && !packer.fitsSize('Medium', box, byId));
  }

  {
    // 4 items (Large + Medium + Small + Small = 3+2+1+1 = 7) already meets the minimum, 1 space left; a Small (cost 1) fits.
    const byId = { a: { size: 'Large' }, b: { size: 'Medium' }, c: { size: 'Small' }, d: { size: 'Small' } };
    const box = {
      ...emptyBox(),
      items: [{ id: 'a', qty: 1 }, { id: 'b', qty: 1 }, { id: 'c', qty: 1 }, { id: 'd', qty: 1 }],
    };
    check('1 space left with 4 items in accepts a Small', packer.remaining(box, byId) === 1 && packer.fitsSize('Small', box, byId));
  }

  check("fmtSlots(3.5) = '3½'", fmtSlots(3.5) === '3½');

  const pass = results.filter((r) => r.pass).length;
  console.info(`[byo-v4] engine self-test: ${pass}/${results.length} passed`);
  results.forEach((r) => console.info(`  ${r.pass ? '✓' : '✗'} ${r.name}`));

  window.__BYOQA_ENGINE__ = { pass, total: results.length, results };
}

if (new URLSearchParams(window.location.search).get('byoqa') === 'engine') {
  runEngineSelfTest();
}

/* Published, not imported. A bare `./byo-v4-core.js` inside another module
   resolves without the `?v=` fingerprint, and Shopify serves that unversioned
   URL with a year of cache -- so the importer gets a second, stale copy and any
   newly added export is simply missing. byo-v4-cart.liquid imports this file
   with its fingerprint, so the namespace below is always the current build.
   See the note in that section, and never import across byo-v4 modules. */
Object.assign((window.BYOV4 = window.BYOV4 || {}), {
  VERSION,
  CONFIG_SELECTOR,
  FAQ_SELECTOR,
  readConfig,
  mergeFaqs,
  money,
  moneyShort,
  words,
  Words,
  countOf,
  TOUCH_RE,
  findProp,
  publicProps,
  readTouchProps,
  slotsOf,
});

export default window.BYO;
